import { ActionCreatorFactory } from 'typescript-fsa'
import { timeout, delay } from '@ynap/promise-utils'
import { push } from 'connected-react-router'
import { asyncFactory, ThunkReturnType, AsyncActionError } from './redux-thunk-action'

import type { State, AuthWcsRequestParams } from '../types/common'

/**
 *  Type of a request function from @ynap/api-utils, accepts a `params` object and returns a `RequestInfo`.
 */
export type RequestFn<T> = (params: T, ...args: any) => RequestInfo

export type FilterParams_<T, P> = T extends RequestFn<infer F & P> ? Omit<F, keyof P> : never

/**
 * Utility type to filter fields from the params object of a `RequestFn`. Given a type `TParams`, this type will remove all
 * fields from that type that are present in `TFilter`.
 */
export type FilterParams<TParams, TFilter> = keyof FilterParams_<TParams, TFilter> extends never
    ? undefined
    : FilterParams_<TParams, TFilter>

/**
 * An authenticated API action creator factory factory.
 *
 * @typeparam `TState` - the shape of the Redux state object
 * @typeparam `TFetch` - a `fetch`-like function that will be passed into the thunk function and used to execute the API calls
 * @param createAction - an instantiated `ActionCreatorFactory` from `typescript-fsa`
 */
export const authApiActionCreatorFactory = <TState extends State = State, TFetch extends typeof fetch = typeof fetch>(
    createAction: ActionCreatorFactory
) => {
    /**
     * Action creator factory function that creates an authenticated api action creators wrapping `fetch`. The actions
     * are created as redux "thunks" and accept a custom `fetch` as the thunk's extra parameter.
     *
     * @typeparam `T` - the shape of the parameters object when calling this action creator function
     * @typeparam `P` - the shape of the result object passed to the done() action
     * @param name - the base name of the action, which will be used to create the started, failed, and done actions
     * @param requestFn - a function accepting a parameters object of type `T & AuthenticatedRequestParams` and returning a `RequestInfo`
     * @returns a thunk action creator function that can be called with `T` and dispatched to redux
     */
    const createAuthApiAction = <TPayload>(name: string) => <TParams extends AuthWcsRequestParams>(
        requestFn: RequestFn<TParams>
    ) => {
        type BaseParams = Pick<TParams, Extract<keyof TParams, keyof AuthWcsRequestParams>> | { uberToken: string }
        type ExtraParams = keyof Omit<TParams, keyof AuthWcsRequestParams> extends never
            ? undefined
            : Omit<TParams, keyof AuthWcsRequestParams>

        const createAsyncAction = asyncFactory<TState, TFetch>(createAction)
        return createAsyncAction<ExtraParams, TPayload, string>(name, async (params, dispatch, getState, fetch) => {
            const { locale, config, router, uberToken: ubertoken } = getState()
            const { location } = router
            const { country } = locale
            const { pathname } = location
            const { clientRequest } = config.head
            const { baseUrl, clientId } = config.global.services.commerce
            const { brandId } = config.global.application

            if (!fetch) {
                throw new Error('Fetch is undefined')
            }

            if (typeof ubertoken == 'undefined') {
                dispatch(push(`/${locale.id}/account/login?redirect=${pathname}`))
                throw new AsyncActionError('Ubertoken was not set')
            }

            const baseParams: BaseParams = {
                baseUrl,
                country,
                clientId,
                brandId,
                uberToken: ubertoken
            }

            const requestParams: BaseParams & ExtraParams = {
                ...baseParams,
                ...params
            }
            const request = requestFn((requestParams as unknown) as TParams)

            return delay(timeout(fetch(request), clientRequest.timeout), clientRequest.delay).then((result) => {
                const { ok, status } = result
                if (ok) {
                    return result.json() as ThunkReturnType<TPayload>
                } else if (status === 401 || status === 403) {
                    dispatch(push(`/${locale.id}/account/login?redirect=${pathname}`))
                    throw new AsyncActionError(`Request failed with HTTP status ${status}`)
                }

                throw new AsyncActionError(result)
            })
        })
    }
    return createAuthApiAction
}
